import React, {useEffect, useLayoutEffect, useRef, useState} from 'react'
import {useAiAssistantDrawerStore} from '../../store/aiAssistantDrawerStore'
import {
  AiAssistantMessage,
  AiAssistantThread,
  useAiAssistant,
} from '../../pages/aiAssistant/hooks/useAiAssistant'

const drawerStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: 24,
  right: 24,
  width: 380,
  maxWidth: 'calc(100vw - 48px)',
  height: 600,
  maxHeight: 'calc(100vh - 120px)',
  background: '#ffffff',
  borderRadius: 12,
  boxShadow: '0 12px 40px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)',
  zIndex: 1050,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}

const headerStyle: React.CSSProperties = {
  padding: '12px 14px',
  borderBottom: '1px solid #e9ecef',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  flexShrink: 0,
}

const listStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
}

const messagesAreaStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: '12px',
  background: '#f5f7fb',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
}

const composerStyle: React.CSSProperties = {
  padding: '8px 10px',
  borderTop: '1px solid #e9ecef',
  display: 'flex',
  alignItems: 'flex-end',
  gap: 8,
  flexShrink: 0,
  background: '#fff',
}

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 14px',
  borderBottom: '1px solid #f1f3f5',
  cursor: 'pointer',
  background: '#fff',
  border: 'none',
  width: '100%',
  textAlign: 'left',
}

const formatRelativeTime = (iso: string | null | undefined) => {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const diffMs = Date.now() - then
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'now'
  if (diffMin < 60) return `${diffMin}m`
  const diffH = Math.floor(diffMin / 60)
  if (diffH < 24) return `${diffH}h`
  const diffD = Math.floor(diffH / 24)
  if (diffD < 7) return `${diffD}d`
  return new Date(iso).toLocaleDateString()
}

const formatTime = (iso: string | null | undefined) => {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})
}

export const AiAssistantDrawer: React.FC = () => {
  const open = useAiAssistantDrawerStore((state) => state.open)
  const close = useAiAssistantDrawerStore((state) => state.close)

  if (!open) return null
  return <AiAssistantDrawerContent onClose={close} />
}

const AiAssistantDrawerContent: React.FC<{onClose: () => void}> = ({onClose}) => {
  const {
    threads,
    loading,
    loadThreads,
    activeThread,
    selectThread,
    createThread,
    deleteThread,
    messages,
    sendMessage,
    sending,
  } = useAiAssistant()

  const [draft, setDraft] = useState('')

  useEffect(() => {
    void loadThreads()
  }, [loadThreads])

  const inConversation = Boolean(activeThread)

  const goBackToList = () => {
    selectThread(null)
    setDraft('')
  }

  const handleOpenThread = (thread: AiAssistantThread) => {
    selectThread(thread)
    setDraft('')
  }

  const handleCreateThread = async () => {
    const thread = await createThread()
    if (thread) setDraft('')
  }

  const handleDeleteThread = async (event: React.MouseEvent, threadId: string) => {
    event.stopPropagation()
    await deleteThread(threadId)
  }

  const handleSend = async () => {
    if (!activeThread) return
    const text = draft.trim()
    if (!text) return
    const ok = await sendMessage(activeThread.id, text)
    if (ok) setDraft('')
  }

  return (
    <div style={drawerStyle} role='dialog' aria-label='AI Assistant'>
      {inConversation && activeThread ? (
        <ConversationView
          thread={activeThread}
          messages={messages}
          loadingMessages={loading}
          draft={draft}
          setDraft={setDraft}
          onSend={handleSend}
          sending={sending}
          onBack={goBackToList}
          onClose={onClose}
        />
      ) : (
        <>
          <div style={headerStyle}>
            <div className='d-flex align-items-center gap-2'>
              <i className='bi bi-stars text-info fs-4' />
              <strong>AI Assistant</strong>
            </div>
            <div className='d-flex align-items-center gap-1'>
              <button
                type='button'
                className='btn btn-sm btn-icon btn-light'
                title='New conversation'
                onClick={() => void handleCreateThread()}
              >
                <i className='bi bi-plus-lg' />
              </button>
              <button
                type='button'
                className='btn btn-sm btn-icon btn-light'
                title='Close'
                onClick={onClose}
              >
                <i className='bi bi-x-lg' />
              </button>
            </div>
          </div>

          <div style={listStyle}>
            {loading && threads.length === 0 && (
              <div className='text-muted text-center py-5'>Loading…</div>
            )}
            {!loading && threads.length === 0 && (
              <div className='text-muted text-center py-5'>
                No conversations yet.
                <div className='mt-2'>
                  <button
                    type='button'
                    className='btn btn-sm btn-primary'
                    onClick={() => void handleCreateThread()}
                  >
                    Start a conversation
                  </button>
                </div>
              </div>
            )}
            {threads.map((thread) => (
              <button
                key={thread.id}
                type='button'
                style={rowStyle}
                onClick={() => handleOpenThread(thread)}
                onMouseEnter={(event) => (event.currentTarget.style.background = '#f8f9fa')}
                onMouseLeave={(event) => (event.currentTarget.style.background = '#fff')}
              >
                <span
                  className='d-inline-flex align-items-center justify-content-center flex-shrink-0'
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: '#0dcaf0',
                    color: '#fff',
                  }}
                >
                  <i className='bi bi-stars' />
                </span>
                <div className='flex-grow-1 min-w-0'>
                  <div className='d-flex justify-content-between align-items-center gap-2'>
                    <span className='fw-bold text-truncate'>{thread.title || 'Conversation'}</span>
                    <span className='text-muted fs-8 flex-shrink-0'>
                      {formatRelativeTime(thread.last_activity)}
                    </span>
                  </div>
                </div>
                <button
                  type='button'
                  className='btn btn-sm btn-icon btn-light flex-shrink-0'
                  title='Delete conversation'
                  onClick={(event) => void handleDeleteThread(event, thread.id)}
                >
                  <i className='bi bi-trash' />
                </button>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

type ConversationViewProps = {
  thread: AiAssistantThread
  messages: AiAssistantMessage[]
  loadingMessages: boolean
  draft: string
  setDraft: (value: string) => void
  onSend: () => Promise<void>
  sending: boolean
  onBack: () => void
  onClose: () => void
}

const ConversationView: React.FC<ConversationViewProps> = ({
  thread,
  messages,
  loadingMessages,
  draft,
  setDraft,
  onSend,
  sending,
  onBack,
  onClose,
}) => {
  const messagesRef = useRef<HTMLDivElement>(null)
  const lastMessageId = messages.length > 0 ? messages[messages.length - 1].id : null

  useLayoutEffect(() => {
    const el = messagesRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [messages, thread.id])

  useEffect(() => {
    if (lastMessageId === null) return
    const el = messagesRef.current
    if (!el) return
    el.scrollTo({top: el.scrollHeight, behavior: 'smooth'})
  }, [lastMessageId])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      void onSend()
    }
  }

  const hasPayload = draft.trim().length > 0

  return (
    <>
      <div style={headerStyle}>
        <div className='d-flex align-items-center gap-2 flex-grow-1' style={{minWidth: 0}}>
          <button
            type='button'
            className='btn btn-sm btn-icon btn-light'
            title='Back'
            onClick={onBack}
          >
            <i className='bi bi-chevron-left' />
          </button>
          <span
            className='d-inline-flex align-items-center justify-content-center flex-shrink-0'
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: '#0dcaf0',
              color: '#fff',
            }}
          >
            <i className='bi bi-stars' />
          </span>
          <div className='min-w-0'>
            <div className='fw-bold text-truncate'>{thread.title || 'Conversation'}</div>
          </div>
        </div>
        <div className='d-flex align-items-center gap-1 flex-shrink-0'>
          <button
            type='button'
            className='btn btn-sm btn-icon btn-light'
            title='Close'
            onClick={onClose}
          >
            <i className='bi bi-x-lg' />
          </button>
        </div>
      </div>

      <div ref={messagesRef} style={messagesAreaStyle}>
        {loadingMessages && messages.length === 0 && (
          <div className='text-muted text-center py-5'>Loading messages…</div>
        )}
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}
        {sending && (
          <div
            style={{
              alignSelf: 'flex-start',
              maxWidth: '80%',
              background: '#ffffff',
              borderRadius: 10,
              padding: '6px 10px',
              boxShadow: '0 1px 0 rgba(17,27,33,0.06)',
              fontSize: '0.85rem',
              fontStyle: 'italic',
              color: '#6c757d',
            }}
          >
            Typing…
          </div>
        )}
      </div>

      <div style={composerStyle}>
        <textarea
          className='form-control form-control-sm'
          placeholder='Message…'
          rows={1}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          style={{resize: 'none', maxHeight: 100}}
        />
        <button
          type='button'
          className='btn btn-sm btn-primary'
          onClick={() => void onSend()}
          disabled={sending || !hasPayload}
          title='Send'
        >
          <i className='bi bi-send-fill' />
        </button>
      </div>
    </>
  )
}

const MessageBubble: React.FC<{message: AiAssistantMessage}> = ({message}) => {
  const isOwn = message.direction === 'inbound'
  const bubbleStyle: React.CSSProperties = {
    alignSelf: isOwn ? 'flex-end' : 'flex-start',
    maxWidth: '80%',
    background: isOwn ? '#d1e7ff' : '#ffffff',
    borderRadius: 10,
    padding: '6px 10px',
    boxShadow: '0 1px 0 rgba(17,27,33,0.06)',
    fontSize: '0.85rem',
    wordBreak: 'break-word',
  }

  return (
    <div style={bubbleStyle}>
      <div style={{whiteSpace: 'pre-wrap'}}>{message.content}</div>
      <div className='text-muted fs-9 text-end' style={{marginTop: 2}}>
        {formatTime(message.created)}
      </div>
    </div>
  )
}
